import { create } from "zustand";
import { supabase } from "./supabase";
import Swal from "sweetalert2";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const REQUIRED_COLUMNS = ["name", "description", "price", "category"];

const useMenuItemsStore = create((set, get) => ({
  meals: [],
  drinks: [],
  filteredMeals: [],
  filteredDrinks: [],
  name: "",
  description: "",
  price: "",
  category: "",
  drink: "",
  categoryDrinks: "",
  editingRow: null,
  rowData: {},
  loadingMeals: true,
  loadingDrinks: true,
  error: "",
  success: "",
  loading: false,
  fileError: "",
  fileSuccess: "",
  fileLoading: false,
  fileData: [],

  // Reset file data
  reset: () => set({ fileData: [], fileError: "", fileSuccess: "" }),

  // ✅ Validate uploaded file type and contents
  validateFile: (file) => {
    console.log("🔍 Starting file validation...", file?.name);

    // Reset state
    set({ fileError: "", fileSuccess: "", fileData: [] });

    if (!file) {
      console.error("⚠️ No file selected.");
      return set({ fileError: "No file selected." });
    }

    const fileType = file.name.split(".").pop().toLowerCase();
    console.log("📂 Detected file type:", fileType);

    // Allow only CSV or Excel formats
    if (!["csv", "xlsx", "xls"].includes(fileType)) {
      console.error("❌ Invalid file type:", fileType);
      set({ fileError: "❌ Only .csv or .xlsx files are supported." });
      return;
    }

    // --- CSV PARSING ---
    if (fileType === "csv") {
      console.log("📄 Parsing CSV file...");
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          console.log(
            "✅ CSV parsed successfully:",
            result.data.length,
            "rows"
          );
          get().validateData(result.data);
        },
        error: (err) => {
          console.error("❌ Error parsing CSV:", err);
          set({ fileError: `CSV parse error: ${err.message}` });
        },
      });
    }

    // --- XLSX PARSING ---
    else {
      console.log("📘 Reading Excel file...");
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "binary" });
          const sheet = workbook.SheetNames[0];
          const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheet]);
          console.log(
            "✅ Excel file parsed successfully:",
            worksheet.length,
            "rows"
          );
          get().validateData(worksheet);
        } catch (err) {
          console.error("❌ Error reading Excel file:", err);
          set({ fileError: "Error reading Excel file." });
        }
      };

      reader.onerror = (err) => {
        console.error("❌ FileReader failed:", err);
        set({ fileError: "Error reading file. Please try again." });
      };

      reader.readAsBinaryString(file);
    }
  },

  // ✅ Validate the data structure and values inside the file
  validateData: async (rows) => {
    console.log("🔎 Starting data validation...");

    // Check for empty file
    if (!rows || !rows.length) {
      console.warn("⚠️ File is empty or could not be parsed.");
      set({ fileError: "❌ The file is empty." });
      return;
    }

    console.log("📊 Raw sample row before normalization:", rows[0]);

    // ✅ Normalize all keys to lowercase + trim spaces from values
    const normalizedRows = rows.map((r) => {
      const newRow = {};
      Object.keys(r).forEach((key) => {
        newRow[key.trim().toLowerCase()] =
          typeof r[key] === "string" ? r[key].trim() : r[key];
      });
      return newRow;
    });

    console.log("🧩 Normalized sample row:", normalizedRows[0]);

    // ✅ Extract headers and verify required columns
    const headers = Object.keys(normalizedRows[0]).map((h) =>
      h.trim().toLowerCase()
    );
    console.log("📋 Headers detected:", headers);

    const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
    if (missing.length > 0) {
      console.error("❌ Missing required columns:", missing);
      set({ fileError: `❌ Missing required columns: ${missing.join(", ")}` });
      return;
    }

    // ✅ Helper to check if a value is truly empty
    const isEmpty = (val) =>
      val === null ||
      val === undefined ||
      (typeof val === "string" && val.trim() === "");

    // ✅ Validate rows
    const invalidRows = normalizedRows.filter((r, i) => {
      const invalid =
        isEmpty(r.name) ||
        // description is optional, but if you want to make it required, uncomment:
        // isEmpty(r.description) ||
        isEmpty(r.category) ||
        r.price === null ||
        r.price === undefined ||
        r.price === "" ||
        isNaN(Number(r.price));

      if (invalid) {
        console.warn(`⚠️ Invalid row at index ${i}:`, r);
      }
      return invalid;
    });

    // ✅ Handle invalid data
    if (invalidRows.length > 0) {
      console.warn("⚠️ Found invalid rows:", invalidRows.length);
      set({
        fileError: `❌ Found ${invalidRows.length} invalid rows. Check name, price, and category.`,
      });
      return;
    }

    // ✅ Validation successful
    console.log(
      "✅ Validation successful. Total valid rows:",
      normalizedRows.length
    );
    set({
      fileData: normalizedRows,
      fileSuccess: `✅ File validated successfully with ${normalizedRows.length} rows.`,
    });

    await get().uploadToSupabase();
  },

  // ✅ Upload valid data to Supabase
  uploadToSupabase: async () => {
    const { fileData, fileLoading } = get();
    console.log("🚀 Starting upload process...");

    if (fileLoading) {
      console.warn("⚠️ Upload already in progress.");
      return;
    }

    if (!fileData.length) {
      console.error("❌ No valid data to upload.");
      set({ fileError: "No valid data to upload." });
      return;
    }

    set({ fileLoading: true, fileError: "", fileSuccess: "" });

    console.log("📤 Uploading", fileData.length, "rows to Supabase...");

    const { error: insertError, data: insertedData } = await supabase
      .from("menuItems")
      .insert(
        fileData.map((d, i) => ({
          item_name: d.name,
          description: d.description,
          price: Number(d.price),
          category: d.category,
          type: "food",
        }))
      );

    if (insertError) {
      console.error("❌ Upload failed:", insertError);
      set({
        fileLoading: false,
        fileError: `❌ Upload failed: ${insertError.message}`,
      });
    } else {
      console.log("✅ Upload complete:", insertedData);
      set({
        fileLoading: false,
        fileSuccess: "✅ Data uploaded successfully!",
        fileData: [],
      });
    }
  },

  // Fetch meals
  fetchMeals: async () => {
    set({ loadingMeals: true });
    try {
      const { data, error } = await supabase.from("menuItems").select("*");
      if (error) throw error;
      set({ meals: data, filteredMeals: data });
    } catch (error) {
      Swal.fire("Error", "Failed to fetch meals.", "error");
    } finally {
      set({ loadingMeals: false });
    }
  },

  // Fetch drinks
  fetchDrinks: async () => {
    set({ loadingDrinks: true });
    try {
      const { data, error } = await supabase.from("drinks").select("*");
      if (error) throw error;
      set({ drinks: data, filteredDrinks: data });
    } catch (error) {
      Swal.fire("Error", "Failed to fetch drinks.", "error");
    } finally {
      set({ loadingDrinks: false });
    }
  },

  // Add a new meal
  handleAddMeal: async () => {
    const { name, description, price, category } = get();
    if (!name || !description || !price || !category) {
      Swal.fire("Error", "Please fill in all fields for the meal.", "error");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("menuItems")
        .insert([{ item_name: name, description, price, category }])
        .select();
      if (error) throw error;

      Swal.fire("Success", "Meal added successfully!", "success");
      set((state) => ({
        meals: [...state.meals, ...data],
        filteredMeals: [...state.filteredMeals, ...data],
        name: "",
        description: "",
        price: "",
        category: "",
      }));
    } catch (error) {
      Swal.fire("Error", "Failed to add meal.", "error");
    }
  },

  // Add a new drink
  handleAddDrink: async () => {
    const { drink, price, categoryDrinks } = get();
    if (!drink || !price || !categoryDrinks) {
      Swal.fire("Error", "Please fill in all fields for the drink.", "error");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("drinks")
        .insert([{ item_name: drink, price, category: categoryDrinks }])
        .select();
      if (error) throw error;

      Swal.fire("Success", "Drink added successfully!", "success");
      set((state) => ({
        drinks: [...state.drinks, ...data],
        filteredDrinks: [...state.filteredDrinks, ...data],
        drink: "",
        price: "",
        categoryDrinks: "",
      }));
    } catch (error) {
      Swal.fire("Error", "Failed to add drink.", "error");
    }
  },

  // Start editing an item
  handleEditStart: (id, row) => {
    set({ editingRow: id, rowData: { ...row } });
  },

  // Handle changes during editing
  handleEditChange: (field, value) => {
    set((state) => ({ rowData: { ...state.rowData, [field]: value } }));
  },

  // Stop editing
  handleEditStop: () => {
    set({ editingRow: null, rowData: {} });
  },

  // Save edited meal
  handleSaveMeal: async (id) => {
    const { rowData } = get();
    try {
      const { error } = await supabase
        .from("menuItems")
        .update({
          item_name: rowData.item_name,
          description: rowData.description,
          price: rowData.price,
          category: rowData.category,
        })
        .eq("id", id);

      if (error) throw error;

      set((state) => ({
        meals: state.meals.map((row) =>
          row.id === id ? { ...row, ...rowData } : row
        ),
        filteredMeals: state.filteredMeals.map((row) =>
          row.id === id ? { ...row, ...rowData } : row
        ),
        editingRow: null,
        rowData: {},
      }));
      Swal.fire("Success", "Meal updated successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to update meal.", "error");
    }
  },

  // Save edited drink
  handleSaveDrink: async (id) => {
    const { rowData } = get();
    try {
      const { error } = await supabase
        .from("drinks")
        .update({
          name: rowData.name,
          price: rowData.price,
          category: rowData.category,
        })
        .eq("id", id);

      if (error) throw error;

      set((state) => ({
        drinks: state.drinks.map((row) =>
          row.id === id ? { ...row, ...rowData } : row
        ),
        filteredDrinks: state.filteredDrinks.map((row) =>
          row.id === id ? { ...row, ...rowData } : row
        ),
        editingRow: null,
        rowData: {},
      }));
      Swal.fire("Success", "Drink updated successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to update drink.", "error");
    }
  },

  // Delete a meal
  handleDeleteMeal: async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { error } = await supabase
            .from("menuItems")
            .delete()
            .eq("id", id);
          if (error) throw error;

          set((state) => ({
            meals: state.meals.filter((row) => row.id !== id),
            filteredMeals: state.filteredMeals.filter((row) => row.id !== id),
          }));
          Swal.fire("Deleted!", "Meal has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error", "Failed to delete meal.", "error");
        }
      }
    });
  },

  // Delete a drink
  handleDeleteDrink: async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { error } = await supabase.from("drinks").delete().eq("id", id);
          if (error) throw error;

          set((state) => ({
            drinks: state.drinks.filter((row) => row.id !== id),
            filteredDrinks: state.filteredDrinks.filter((row) => row.id !== id),
          }));
          Swal.fire("Deleted!", "Drink has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error", "Failed to delete drink.", "error");
        }
      }
    });
  },
}));

export default useMenuItemsStore;
